/* ══════════════════════════════════════════════
 *  React-Query hooks for the Document Explorer
 * ══════════════════════════════════════════════ */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { ROLE_RANK, rankOf } from '../utils/roleHierarchy';
import type {
  ListObjectsResponse,
  CreateFolderPayload,
  DeleteObjectsPayload,
  RenameObjectPayload,
  DownloadZipPayload,
  ParseEmailPayload,
  ParsedEmail,
  ExplorerPermissions,
} from '../types/explorer';

const BUCKET = import.meta.env.VITE_S3_BUCKET_NAME || 'justizia-case-file';
const DELIMITER = '/';

/* ─── List Objects ─── */
export function useListObjects(
  prefix: string,
  enabledOrOptions?: boolean | { enabled?: boolean; includeLedger?: boolean },
) {
  const enabled =
    typeof enabledOrOptions === 'boolean' ? enabledOrOptions : (enabledOrOptions?.enabled ?? true);
  const includeLedger =
    typeof enabledOrOptions === 'object' && enabledOrOptions !== null
      ? (enabledOrOptions.includeLedger ?? false)
      : false;
  return useQuery<ListObjectsResponse>({
    queryKey: ['explorer', 'list', prefix, includeLedger],
    queryFn: async () => {
      const { data } = await api.post('/api/explorer/listObjects', {
        bucket: BUCKET,
        prefix,
        delimiter: DELIMITER,
        ...(includeLedger ? { includeLedger: true } : {}),
      });
      return data;
    },
    enabled: enabled && !!prefix,
    staleTime: 0, // always re-fetch on navigate
  });
}

/* ─── Permissions check ─── */
export function useExplorerPermissions() {
  return useQuery<ExplorerPermissions>({
    queryKey: ['explorer', 'permissions'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/api/users/permissions');
        // Backend returns { role, permissions: [{ feature_name, ... }] }
        const role: string = data.role || 'user';
        if (rankOf(role) >= ROLE_RANK.admin) {
          return { canDeleteDocuments: true };
        }
        const perms: { feature_name: string }[] = Array.isArray(data.permissions)
          ? data.permissions
          : [];
        return { canDeleteDocuments: perms.some((p) => p.feature_name === 'delete_document') };
      } catch {
        return { canDeleteDocuments: false };
      }
    },
    staleTime: 5 * 60 * 1000,
  });
}

/* ─── Create Folder ─── */
export function useCreateFolder(prefix: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateFolderPayload) => {
      const { data } = await api.post('/api/explorer/createFolder', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['explorer', 'list', prefix] }),
  });
}

/* ─── Delete Objects ─── */
export function useDeleteObjects(prefix: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DeleteObjectsPayload) => {
      const { data } = await api.post('/api/explorer/deleteObjects', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['explorer', 'list', prefix] }),
  });
}

/* ─── Rename Object ─── */
export function useRenameObject(prefix: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RenameObjectPayload) => {
      const { data } = await api.post('/api/explorer/renameObject', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['explorer', 'list', prefix] }),
  });
}

/* ─── Categorise Documents (PATCH /api/explorer/documents/categorise) ─── */
export function useCategoriseDocuments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { s3Keys: string[]; categoryId: number | null }) => {
      const { data } = await api.patch('/api/explorer/documents/categorise', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['explorer', 'list'] }),
  });
}

/* ─── Get Signed URL (for viewing / downloading a file) ─── */
export async function getSignedUrl(key: string, disposition?: string): Promise<string> {
  const { data } = await api.post('/api/explorer/getSignedUrl', {
    bucket: BUCKET,
    key,
    ...(disposition ? { disposition } : {}),
  });
  return data.url ?? data;
}

/* ─── Create short URL for Office Online ─── */
export async function createShortUrl(key: string): Promise<string> {
  const { data } = await api.post('/api/document-proxy/create-short-url', {
    bucket: BUCKET,
    key,
  });
  return data.shortUrl ?? data.url ?? data;
}

/* ─── Download Zip ─── */
export async function downloadZip(payload: DownloadZipPayload): Promise<Blob> {
  const { data } = await api.post('/api/explorer/downloadZip', payload, {
    responseType: 'blob',
  });
  return data;
}

/* ─── Upload chunks ─── */
export interface ChunkUploadProgress {
  fileName: string;
  percent: number;
  done: boolean;
  error?: string;
}

function generateClientUploadId(): string {
  return `client-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

type ApiLikeError = {
  message?: string;
  code?: string;
  response?: {
    status?: number;
    data?: {
      error?: string;
      message?: string;
      requestId?: string;
    };
  };
};

function classifyUploadError(err: unknown): string {
  const apiErr = err as ApiLikeError;
  const status = apiErr.response?.status;
  const backendError = apiErr.response?.data?.error || apiErr.response?.data?.message;
  const requestId = apiErr.response?.data?.requestId;
  const rawMessage = backendError || apiErr.message || 'Upload failed';

  if (status === 401) {
    return requestId
      ? `Your session is invalid or expired. Please sign in again and retry. (request ${requestId})`
      : 'Your session is invalid or expired. Please sign in again and retry.';
  }

  if (status === 403 || /not allowed by cors/i.test(rawMessage)) {
    return requestId
      ? `Upload blocked by origin policy (CORS). Please report this to support with request ${requestId}.`
      : 'Upload blocked by origin policy (CORS). Please refresh and try again.';
  }

  if (/network error/i.test(rawMessage)) {
    return 'Network interruption during upload. Check connection/VPN and retry.';
  }

  if (/timed out|timeout/i.test(rawMessage)) {
    return 'Upload timed out. Please retry, ideally with a stable network connection.';
  }

  return rawMessage;
}

/** Compute a SHA-256 hex digest of a File using the Web Crypto API */
export async function computeSHA256(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * @deprecated This chunked upload method is deprecated as of Phase 4.
 * Use uploadFileDirectWithRetry() instead for better performance and reliability.
 *
 * The backend endpoint /api/explorer/uploadChunk has been removed.
 * This function is kept only for reference during migration.
 *
 * Legacy chunked upload function - splits file into chunks and uploads via backend
 */
export async function uploadFileChunked(
  _file: File,
  _key: string,
  _uploadedBy: string,
  _onProgress: (p: ChunkUploadProgress) => void,
  _options?: { categoryId?: number | null; contentHash?: string },
): Promise<void> {
  throw new Error(
    'uploadFileChunked is deprecated - use uploadFileDirectWithRetry instead. ' +
      'The backend endpoint /api/explorer/uploadChunk has been removed.',
  );
}

// ============================================================================
// PRESIGNED URL UPLOAD (Phase 4 - Direct to S3)
// ============================================================================

/**
 * Upload file to S3 using presigned URL with XMLHttpRequest for progress tracking
 * @param file - File to upload
 * @param presignedUrl - Presigned PUT URL from backend
 * @param contentType - MIME type
 * @param onProgress - Progress callback (0-100)
 * @returns Promise that resolves when upload completes
 */
export function uploadToS3WithProgress(
  file: File,
  presignedUrl: string,
  contentType: string,
  _uploadedBy: string,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open('PUT', presignedUrl, true);
    xhr.withCredentials = false;
    xhr.setRequestHeader('Content-Type', contentType);

    // Track upload progress
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        onProgress(percent);
      }
    };

    // Upload complete
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        const responseText = xhr.responseText || '';
        const codeMatch = responseText.match(/<Code>([^<]+)<\/Code>/i);
        const messageMatch = responseText.match(/<Message>([^<]+)<\/Message>/i);
        const s3Code = codeMatch?.[1];
        const s3Message = messageMatch?.[1];

        if (s3Code) {
          reject(
            new Error(
              `S3 upload failed (${xhr.status}) [${s3Code}]${s3Message ? ` ${s3Message}` : ''}`,
            ),
          );
          return;
        }

        reject(new Error(`S3 upload failed with status ${xhr.status}`));
      }
    };

    // Network error
    xhr.onerror = () => {
      reject(new Error('Network error during S3 upload (request blocked or interrupted)'));
    };

    // Timeout (5 minutes for large files)
    xhr.ontimeout = () => {
      reject(new Error('S3 upload timed out'));
    };
    xhr.timeout = 5 * 60 * 1000;

    // Send the file
    xhr.send(file);
  });
}

/**
 * Fallback upload path for environments that intermittently break presigned PUT requests.
 * Uploads bytes through backend legacy endpoint, which then writes to S3 server-side.
 */
async function uploadViaBackend(
  file: File,
  key: string,
  contentType: string,
  uploadedBy: string,
): Promise<void> {
  const form = new FormData();
  form.append('bucket', BUCKET);
  form.append('key', key);
  form.append('contentType', contentType);
  form.append('file', file);
  form.append('uploadedBy', uploadedBy);

  const { data } = await api.post('/api/explorer/uploadObject', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  if (!data?.success) {
    throw new Error(data?.message || 'Backend fallback upload failed');
  }
}

/**
 * Get PDF page count by parsing the PDF structure
 * @param file - PDF file
 * @returns Page count or null if unable to determine
 */
export async function getPdfPageCount(file: File): Promise<number | null> {
  if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
    return null;
  }

  try {
    // Read first 500KB of file (enough to find page count in most PDFs)
    const buffer = await file.slice(0, 500_000).arrayBuffer();
    const bytes = new Uint8Array(buffer);

    // Convert to text in chunks to avoid stack overflow
    const CHUNK = 8192;
    const chunks: string[] = [];
    for (let i = 0; i < bytes.length; i += CHUNK) {
      chunks.push(String.fromCharCode(...bytes.subarray(i, i + CHUNK)));
    }
    const text = chunks.join('');

    // Look for /Type /Pages ... /Count N pattern
    const match = text.match(/\/Type\s*\/Pages[\s\S]{0,300}?\/Count\s+(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  } catch {
    return null;
  }
}

/**
 * Upload file directly to S3 using presigned URL (Phase 4 - NEW)
 * This replaces the chunked upload for better performance and scalability
 *
 * @param file - File to upload
 * @param key - S3 key (full path)
 * @param caseRef - Case reference
 * @param caseType - Case type slug
 * @param uploadedBy - Username
 * @param onProgress - Progress callback
 * @param options - Optional metadata (categoryId, contentHash, pageCount)
 * @returns Promise with upload result
 */
export async function uploadFileDirect(
  file: File,
  key: string,
  caseRef: string,
  caseType: string,
  uploadedBy: string,
  onProgress: (p: ChunkUploadProgress) => void,
  options?: { categoryId?: number | null; contentHash?: string; pageCount?: number | null },
): Promise<{ success: boolean; ledgerWriteSuccess: boolean; error?: string }> {
  const clientUploadId = generateClientUploadId();

  try {
    // 1. Request presigned URL from backend
    const initiateResponse = await api.post(
      '/api/explorer/initiate-upload',
      {
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type || 'application/octet-stream',
        caseRef,
        s3Key: key,
        uploadedBy,
        categoryId: options?.categoryId,
        clientUploadId,
      },
      {
        headers: {
          'X-Client-Upload-Id': clientUploadId,
        },
      },
    );

    if (!initiateResponse.data.success) {
      throw new Error(initiateResponse.data.error || 'Failed to initiate upload');
    }

    const { uploadId, presignedUrl } = initiateResponse.data;

    // 2. Upload directly to S3 with progress tracking
    let usedBackendFallback = false;
    try {
      await uploadToS3WithProgress(
        file,
        presignedUrl,
        file.type || 'application/octet-stream',
        uploadedBy,
        (percent) => {
          onProgress({ fileName: file.name, percent, done: false });
        },
      );
    } catch (uploadErr: unknown) {
      const uploadMsg = uploadErr instanceof Error ? uploadErr.message : String(uploadErr);

      // Targeted mitigation for intermittent S3 signing/header mismatch observed in production.
      if (
        /SignatureDoesNotMatch/i.test(uploadMsg) ||
        /There were headers present in the request which were not signed/i.test(uploadMsg) ||
        /\[AccessDenied\]/i.test(uploadMsg)
      ) {
        onProgress({
          fileName: file.name,
          percent: 0,
          done: false,
          error: 'Direct S3 upload failed (signing mismatch). Retrying via fallback...',
        });

        await uploadViaBackend(file, key, file.type || 'application/octet-stream', uploadedBy);
        usedBackendFallback = true;
      } else {
        throw uploadErr;
      }
    }

    if (usedBackendFallback) {
      onProgress({ fileName: file.name, percent: 100, done: true });
      return { success: true, ledgerWriteSuccess: true };
    }

    // 3. Confirm upload with backend (verify S3 + write to ledger)
    const confirmResponse = await api.post(
      '/api/explorer/confirm-upload',
      {
        uploadId,
        s3Key: key,
        fileName: file.name,
        fileSize: file.size,
        caseRef,
        caseType,
        uploadedBy,
        categoryId: options?.categoryId,
        contentHash: options?.contentHash,
        pageCount: options?.pageCount,
        clientUploadId,
      },
      {
        headers: {
          'X-Client-Upload-Id': clientUploadId,
        },
      },
    );

    // Mark as done only after backend confirms
    const result = confirmResponse.data;
    onProgress({
      fileName: file.name,
      percent: 100,
      done: result.success,
      error: result.success ? undefined : result.error,
    });

    return result;
  } catch (err: unknown) {
    const errorMsg = classifyUploadError(err);

    onProgress({
      fileName: file.name,
      percent: 0,
      done: false,
      error: errorMsg,
    });

    throw new Error(errorMsg);
  }
}

/**
 * Upload file with retry logic (3 attempts with exponential backoff)
 * This is the main entry point for presigned URL uploads
 *
 * @param file - File to upload
 * @param key - S3 key (full path)
 * @param caseRef - Case reference
 * @param caseType - Case type slug
 * @param uploadedBy - Username
 * @param onProgress - Progress callback
 * @param options - Optional metadata
 * @returns Promise with upload result
 */
export async function uploadFileDirectWithRetry(
  file: File,
  key: string,
  caseRef: string,
  caseType: string,
  uploadedBy: string,
  onProgress: (p: ChunkUploadProgress) => void,
  options?: { categoryId?: number | null; contentHash?: string; pageCount?: number | null },
): Promise<{ success: boolean; ledgerWriteSuccess: boolean; error?: string }> {
  const MAX_RETRIES = 3;
  const RETRY_DELAYS = [1000, 2000, 4000]; // 1s, 2s, 4s

  let lastError: Error | null = null;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const result = await uploadFileDirect(
        file,
        key,
        caseRef,
        caseType,
        uploadedBy,
        onProgress,
        options,
      );

      // Success! Return result
      return result;
    } catch (err: unknown) {
      lastError = err as Error;

      // If not the last attempt, wait before retrying
      if (attempt < MAX_RETRIES - 1) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]));

        // Update UI to show retry
        onProgress({
          fileName: file.name,
          percent: 0,
          done: false,
          error: `Upload failed, retrying... (attempt ${attempt + 2}/${MAX_RETRIES})`,
        });
      }
    }
  }

  // All retries exhausted
  const errorMsg = lastError?.message || 'Upload failed after 3 retries';
  onProgress({
    fileName: file.name,
    percent: 0,
    done: false,
    error: errorMsg,
  });

  throw new Error(errorMsg);
}

/* ─── Check Duplicate ─── */
export interface DuplicateCheckResult {
  isDuplicate: boolean;
  existing: Array<{
    document_id: string;
    file_name: string;
    case_ref: string | null;
    generated_at: string;
    s3_key: string;
  }>;
}

export function useCheckDuplicate() {
  return useMutation({
    mutationFn: async ({
      contentHash,
      caseRef,
    }: {
      contentHash: string;
      caseRef?: string;
    }): Promise<DuplicateCheckResult> => {
      const { data } = await api.post('/api/explorer/check-duplicate', { contentHash, caseRef });
      return data;
    },
  });
}

/* ─── Parse Email ─── */
export async function parseEmail(payload: ParseEmailPayload): Promise<ParsedEmail> {
  const { data: res } = await api.post('/api/explorer/parseEmail', payload);
  // Backend returns { success, data: { headers: { from, to, ... }, body: { html, text }, attachments, parseMethod } }
  const d = res.data ?? res;
  const h = d.headers ?? {};
  const b = d.body ?? {};
  return {
    from: h.from,
    to: h.to,
    cc: h.cc,
    bcc: h.bcc,
    subject: h.subject,
    date: h.date,
    messageId: h.messageId,
    htmlBody: b.html,
    textBody: b.text,
    attachments: (d.attachments ?? []).map(
      (a: { fileName?: string; filename?: string; contentType?: string; size?: number }) => ({
        filename: a.fileName ?? a.filename ?? 'unnamed',
        contentType: a.contentType,
        size: a.size,
      }),
    ),
    parseMethod: d.parseMethod,
  };
}

/* ─── Bucket constant exported for payloads ─── */
export { BUCKET };
